import { Link, SvgIcon, SxProps, Tooltip, Typography } from '@mui/material';
import React, { ReactNode, useMemo, useState } from 'react';
import type { Contact, ContactInfo, ContactInput } from 'sr-types/lib/person/v1/graphql';
import type { AlternateIdInput } from 'sr-types/lib/organization/v1/graphql';
import { BASIC_INFO_FORM_TYPE } from '../../../person/detailsPage/editComponents/BasicInfoFormUtils';
import { SECURITY_ACTIONS } from '../../auth/Security';
import type { SecuredActionType } from '../../components/ActionType';
import EllipsisText from '../../components/EllipsisText';
import ResponsiveActions from '../../components/ResponsiveActions';
import { findAlternateId } from '../../components/utils/alternateIdFormUtils';
import { IMDB_LABEL, LINKEDIN_LABEL, STUDIO_SYSTEM_LABEL } from '../../constants/alternateIdConstants';
import { ENTITY_CONSTANTS } from '../../constants/entityConstants';
import I18n from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import { H, V } from '../../Layout';
import { useStyling } from '../../Theme';
import PreviewBusinessContacts from './PreviewBusinessContacts';
import PreviewContactInfo from './PreviewContactInfo';

type ExternalLink = string | { url?: string };

type InfoContainerItem = {
    name: string;
    isSharedInDirectory?: boolean;
    isPrimary?: boolean;
    role?: string;
    contactInfo?: ContactInfo;
    businessContacts?: Array<Contact | ContactInput>;
    location?: string;
    websiteURL?: string;
    alternateIds?: AlternateIdInput[];
    Imdb?: ExternalLink;
    LinkedIn?: ExternalLink;
    StudioSystem?: ExternalLink;
};

type InfoContainerProps = {
    item: InfoContainerItem;
    editEnabled: boolean;
    showArrowRight?: boolean;
    onEditPerson?: (editFieldName: string) => void;
    previewRole?: boolean;
    onAddBusinessContact?: () => void;
    onViewBusinessContacts?: () => void;
    additionalIcons?: ReactNode[];
    isOrganizationProfile?: boolean;
    handleEditOrganization?: () => void;
    previewOrganizationName?: string;
    /** Optional email/phone icon styling for the contact preview row. */
    previewContactIconSx?: SxProps;
    nameSx?: SxProps;
};

const getExternalLinkUrl = (link?: ExternalLink): string | undefined => {
    return typeof link === 'string' ? link : link?.url;
};

export const InfoContainer = ({
    item,
    editEnabled,
    showArrowRight = false,
    onEditPerson = undefined,
    previewRole = false,
    onAddBusinessContact = () => {},
    onViewBusinessContacts = undefined,
    additionalIcons = [],
    isOrganizationProfile = false,
    handleEditOrganization = () => {},
    previewOrganizationName,
    previewContactIconSx = undefined,
    nameSx = undefined
}: InfoContainerProps) => {
    const { theme, isMobile } = useStyling();
    const [isEdit, setIsEdit] = useState(false);
    const businessContacts = (item.businessContacts || []) as ContactInput[];
    const previewSubtitle = previewRole ? item.role : undefined;

    const IMDbLink =
        (item.alternateIds && item.alternateIds.length && findAlternateId(item.alternateIds, IMDB_LABEL)) || item.Imdb;
    const linkedinLink =
        (item.alternateIds && item.alternateIds.length && findAlternateId(item.alternateIds, LINKEDIN_LABEL)) ||
        item.LinkedIn;
    const studioSystemLink =
        (item.alternateIds && item.alternateIds.length && findAlternateId(item.alternateIds, STUDIO_SYSTEM_LABEL)) ||
        item.StudioSystem;

    const openURLInNewTab = (url?: string) => {
        if (!url) {
            return;
        }

        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
    };

    const securedActions = useMemo<SecuredActionType[]>(
        () => [
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: isOrganizationProfile ? ENTITY_CONSTANTS.organization : ENTITY_CONSTANTS.person,
                label: <I18n token="form.title.edit" />,
                icon: Icons.Edit,
                onClick: () => {
                    isOrganizationProfile ? handleEditOrganization() : onEditPerson?.(BASIC_INFO_FORM_TYPE.name);
                }
            }
        ],
        [handleEditOrganization, isOrganizationProfile, onEditPerson]
    );

    return (
        <V
            gap={0.5}
            onMouseEnter={() => editEnabled && setIsEdit(true)}
            onMouseLeave={() => {
                editEnabled && setIsEdit(false);
            }}
        >
            <H
                sx={{
                    fontSize: '20px',
                    justifyContent: ((editEnabled && isMobile) || showArrowRight) && 'space-between'
                }}
            >
                <V sx={{ width: isMobile ? 'unset' : 'fit-content' }}>
                    <H data-testid={'info-container-name'} sx={{ gap: 0.5 }}>
                        <EllipsisText breakAll text={item.name} sx={nameSx} />
                        {item?.isSharedInDirectory ? <Icons.ShowrunnrMark /> : <></>}
                        {item?.isPrimary ? (
                            <SvgIcon component={Icons.Star} sx={{ color: theme.palette.primary.main }} />
                        ) : (
                            <></>
                        )}
                    </H>
                    {previewSubtitle ? (
                        <Typography color={theme.palette.text.primary} noWrap title={previewSubtitle}>
                            {previewSubtitle}
                        </Typography>
                    ) : (
                        <></>
                    )}
                </V>

                {editEnabled ? (
                    <ResponsiveActions
                        isActivated={isEdit}
                        securedActions={securedActions}
                        containerSx={{ p: 0 }}
                    />
                ) : (
                    <></>
                )}
                {showArrowRight ? (
                    <SvgIcon
                        component={Icons.ArrowForward}
                        sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                    />
                ) : (
                    <></>
                )}
            </H>

            <H gap={1} sx={{ width: 'unset' }}>
                <PreviewContactInfo
                    contactInfo={item.contactInfo}
                    businessContacts={businessContacts}
                    organizationName={previewOrganizationName}
                    previewContactIconSx={previewContactIconSx}
                />

                <PreviewBusinessContacts
                    editEnabled={isOrganizationProfile ? false : editEnabled}
                    businessContacts={businessContacts}
                    onAddBusinessContact={onAddBusinessContact}
                    onViewBusinessContacts={onViewBusinessContacts}
                />
            </H>

            {item.location ? (
                <H sx={{ gap: 1, fontSize: '12px', color: `${theme.palette.text.secondary}` }}>
                    <SvgIcon
                        component={Icons.LocationOutlined}
                        sx={{ color: theme.palette.action.active, width: '18px', height: '18px' }}
                    />
                    <EllipsisText text={item.location} />
                </H>
            ) : (
                <></>
            )}
            <H gap={0.5}>
                {linkedinLink || IMDbLink || studioSystemLink ? (
                    <>
                        {linkedinLink && (
                            <SvgIcon
                                component={Icons.LinkedIn}
                                sx={{ color: `${theme.palette.text.secondary}`, cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openURLInNewTab(getExternalLinkUrl(linkedinLink));
                                }}
                            />
                        )}
                        {IMDbLink && (
                            <SvgIcon
                                component={Icons.IMDb}
                                sx={{ color: `${theme.palette.text.secondary}`, cursor: 'pointer' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openURLInNewTab(getExternalLinkUrl(IMDbLink));
                                }}
                            />
                        )}
                        {studioSystemLink && (
                            <Tooltip title={<I18n token="common.studio.system" />}>
                                <SvgIcon
                                    data-testid="studio-system-link-icon"
                                    component={Icons.Link}
                                    sx={{ color: `${theme.palette.text.secondary}`, cursor: 'pointer' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openURLInNewTab(getExternalLinkUrl(studioSystemLink));
                                    }}
                                />
                            </Tooltip>
                        )}
                    </>
                ) : (
                    <></>
                )}
                {additionalIcons.map((icon, index) => (
                    <React.Fragment key={index}>{icon}</React.Fragment>
                ))}
            </H>
            {item.websiteURL ? (
                <V sx={{ gap: 1 }}>
                    <H
                        data-testid={'info-container-website'}
                        sx={{ gap: 1, fontSize: '12px', color: `${theme.palette.text.secondary}` }}
                    >
                        <SvgIcon
                            component={Icons.Language}
                            sx={{ color: `${theme.palette.text.secondary}`, width: '16px', height: '16px' }}
                        />
                        <Link
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            className="clickable"
                            target={'_blank'}
                            href={item.websiteURL}
                            rel="noopener"
                            sx={{ color: 'inherit', textDecoration: 'inherit' }}
                        >
                            <EllipsisText text={item.websiteURL} />
                        </Link>
                    </H>
                </V>
            ) : (
                <></>
            )}
        </V>
    );
};
